import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { faqs } from "../../Config/features";

const CATEGORIES = [
  "All",
  "General",
  "Templates & Files",
  "Quality & Export",
  "Subscription",
  "AI & Smart",
] as const;

type Category = (typeof CATEGORIES)[number];

const FAQExplorer: React.FC = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<Category>("All");
  const [openId, setOpenId] = useState<string | null>(null);

  // Plain filter — runs fresh on every render (26 items, no perf concern)
  const q = query.trim().toLowerCase();
  const filtered = faqs.filter((f) => {
    // Category filter
    if (activeCategory !== "All" && f.category !== activeCategory) return false;
    // Search filter
    if (q) {
      const inQuestion = t(f.question).toLowerCase().includes(q);
      const inAnswer   = t(f.answer).toLowerCase().includes(q);
      if (!inQuestion && !inAnswer) return false;
    }
    return true;
  });

  return (
    <section className="fq-explorer">
      {/* ── Search + filters ── */}
      <div className="fq-explorer__search">
        <div className="fq-explorer__search-glow" />
        <div className="fq-explorer__search-bar">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions…"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")}>
              Clear
            </button>
          )}
        </div>

        <div className="fq-explorer__filters">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`fq-explorer__filter-btn${activeCategory === cat ? " active" : ""}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ── FAQ list ── */}
      <div className="fq-explorer__list">
        {filtered.map((f) => {
          const id = f.question; // e.g. "Q1", unique and stable across filtering
          const isOpen = openId === id;
          return (
            <div key={id} className={`fq-explorer__item${isOpen ? " open" : ""}`}>
              <span className="fq-explorer__item-bar" />
              <button
                type="button"
                className="fq-explorer__q"
                onClick={() => setOpenId(isOpen ? null : id)}
                aria-expanded={isOpen}
              >
                <span className={`fq-explorer__plus${isOpen ? " open" : ""}`} aria-hidden="true">
                  +
                </span>
                <div className="fq-explorer__q-content">
                  <span className="fq-explorer__q-cat">{f.category.toUpperCase()}</span>
                  <span className="fq-explorer__q-text">{t(f.question)}</span>
                </div>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`fq-explorer__chevron${isOpen ? " open" : ""}`}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              <div
                className="fq-explorer__a-wrap"
                style={{ maxHeight: isOpen ? 300 : 0, opacity: isOpen ? 1 : 0 }}
              >
                <p className="fq-explorer__a">{t(f.answer)}</p>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="fq-explorer__empty">
            {query
              ? <>No results for &quot;<strong>{query}</strong>&quot;. Try a different keyword.</>
              : <>No questions in this category yet.</>
            }
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQExplorer;
